/** REST over history + world state (`airsec/api/app.py`).
 *
 * Live state arrives on the socket (`ws.ts`); this file is the *past* — and the one-shot
 * reads a panel needs before the first frame lands. Every url is relative: `vite dev`
 * proxies them to the backend and production serves both from one origin.
 */

import type { Altitude, Flag, Viewport } from './types.ts'

// Empty = same origin, which is the normal case (vite proxies in dev, FastAPI serves in prod).
// Set it when the UI is served from a different origin than the API, or from a test harness
// with no origin at all.
let base = ''

/** Point REST at an explicit origin, e.g. `setApiBase('http://127.0.0.1:8137')`. */
export function setApiBase(url: string): void {
  base = url.replace(/\/$/, '')
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(base + path, { cache: 'no-store' })
  if (!res.ok) {
    // Direction, not mood: name the call that failed so the panel can say what to do.
    throw new Error(`${res.status} ${res.statusText} — ${path}`)
  }
  return res.json() as Promise<T>
}

const q = (params: Record<string, unknown>): string => {
  const s = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v == null) continue
    if (Array.isArray(v)) for (const one of v) s.append(k, String(one))
    else s.set(k, String(v))
  }
  const out = s.toString()
  return out ? `?${out}` : ''
}

// ---------------------------------------------------------------- entity dossiers

export interface PersonTimeline {
  /** Interaction events, oldest first. */
  events: Array<Record<string, any>>
  /** Per-camera observation segments. */
  tracks: Array<Record<string, any>>
  [k: string]: any
}

/** Full walkthrough story for one person (B7: <=200ms). */
export function personTimeline(gid: string): Promise<PersonTimeline> {
  return get<PersonTimeline>(`/history/person/${encodeURIComponent(gid)}`)
}

export interface CustodyRow {
  t: number
  type: string
  person?: string | null
  zone?: string | null
  provenance?: { frames_ref?: string[]; [k: string]: any } | null
  [k: string]: any
}

/** Custody chain for one item — every person who carried, placed or took it. */
export function itemCustody(gid: string): Promise<CustodyRow[]> {
  return get<CustodyRow[]>(`/history/item/${encodeURIComponent(gid)}`)
}

// ---------------------------------------------------------------- world + history

export interface SnapshotResponse {
  t: number
  altitude: Altitude
  viewport: Viewport | null
  counts: Record<string, number>
  zones: Record<string, number>
  truncated: boolean
  tracks: Array<{ gid: string; kind: string; x: number; y: number; [k: string]: any }>
}

/** One-shot world state. The socket supersedes this — use it only before the first frame. */
export function snapshot(
  vp?: Viewport | null,
  altitude: Altitude = 'zone',
  limit?: number,
): Promise<SnapshotResponse> {
  const [x0, y0, x1, y1] = vp ?? []
  return get<SnapshotResponse>(`/snapshot${q({ x0, y0, x1, y1, altitude, limit })}`)
}

/** Raw interaction events in a time window (B8: <=1s across 24h). */
export function events(
  t0: number,
  t1: number,
  opts: { zone?: string; types?: string[]; gid?: string } = {},
): Promise<Array<Record<string, any>>> {
  return get(`/history/events${q({ t0, t1, ...opts })}`)
}

/** Aggregated counts over a window — the fast path when the raw events are not needed. */
export function rollup(
  t0: number,
  t1: number,
  opts: { zone?: string; types?: string[] } = {},
): Promise<Record<string, any>> {
  return get(`/history/rollup${q({ t0, t1, ...opts })}`)
}

export interface Health {
  ok: boolean
  t: number
  /**
   * PRESENT non-group entities. It used to be registry SIZE — 989 against 44 actually on
   * the floor — because departed rows were never collected, which made the operator's
   * primary curl the least true number in the system (soak/BT-5 fixed it at the source).
   */
  tracks: number
  /**
   * All present entities including groups; equals `/snapshot` `counts.total`.
   * Optional because a server predating soak's health-body fix does not send it, and a
   * MISSING field must read as unknown rather than as zero.
   */
  entities?: number
  /**
   * Rows still in the registry. Reported ALONGSIDE `tracks` on purpose: a truer headline
   * that hid the backlog would make the next leak invisible, so the gap between these two
   * IS the leak, on the surface, for free. Optional for the same reason as `entities`.
   */
  registry_rows?: number
  /** Ops additions (self-healing harness): absent on a bare airsec.api app. */
  zone_attribution?: boolean
  facility?: string
}

/** Backend liveness. Cheap enough to poll; the socket is the real health signal. */
export function health(): Promise<Health> {
  return get<Health>('/health')
}

/**
 * Graded flags fired since `since`, as served by the demo spine (`scripts/run_demo.py`).
 * Live flags ride the control lane — prefer `useFlags()`. This is the backfill path for a
 * tab that connected after a finding fired. Resolves to an empty page when the endpoint is
 * absent (a plain `uvicorn airsec.api.app:app` has no /demo routes).
 */
/** What is serving this page (`/demo/info`): rung, facility, pipeline source, caveats. */
export interface DemoInfo {
  rung?: string
  facility?: string
  /** Pipeline provenance, verbatim — e.g. "simulator" vs "MTMC ground-truth annotation replay". */
  source?: string
  cameras?: number
  zones?: number
  sim_t?: number
  scenario?: string
  pack?: string
  is_detection?: boolean
  /** Honest limits of this rung's data (Rung B ships several). */
  caveats?: string[]
  [k: string]: unknown
}

/**
 * Provenance of the serving backend, or null when unknowable (endpoint absent/unreachable).
 * Callers render null as "unknown" — NEVER substitute a plausible default: a wrong
 * provenance string is worse than a missing one.
 */
export async function demoInfo(): Promise<DemoInfo | null> {
  try {
    return await get<DemoInfo>('/demo/info')
  } catch {
    return null
  }
}

/**
 * The provenance line the shell footer renders — pure and exported so the wire gate can
 * assert the footer says what the API reported (a hardcoded string passed every earlier
 * test; that was the finding).
 */
export function fmtProvenance(info: DemoInfo | null): string {
  if (!info?.rung || !info.source) return 'rung unknown · provenance unavailable'
  return `rung ${String(info.rung).toUpperCase()} · ${info.facility ?? 'unknown facility'} · ${info.source}`
}

export async function flagsSince(since = 0): Promise<{ next: number; flags: Flag[] }> {
  try {
    return await get<{ next: number; flags: Flag[] }>(`/demo/flags${q({ since })}`)
  } catch (e) {
    // Swallow ONLY the endpoint-absent case; a network/server failure must surface, or a
    // panel would render "no findings" over a broken backend.
    if (e instanceof Error && e.message.startsWith('404')) return { next: since, flags: [] }
    throw e
  }
}
